// src/features/dashboard/components/CustomersChart.tsx
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Users } from 'lucide-react';
import { GlassCard } from '../../../app/components/ui/GlassCard';
import { ChartCardSkeleton } from './ui/Skeleton';
import { formatNumber } from '../utils/formatters';
import type { WeeklyCustomersPoint } from '../types/dashboard.types';

interface CustomersChartProps {
  data: WeeklyCustomersPoint[];
  isInitialLoading: boolean;
}

function CustomersTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="vania-chart-tooltip">
      <span className="text-xs text-[var(--vania-text-tertiary,#6b7280)]">{label}</span>
      {payload.map((entry: any) => (
        <span key={entry.dataKey} className="flex items-center gap-1.5 text-sm font-semibold text-[var(--vania-text-primary,#f5f6fa)]">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: entry.color }} />
          {formatNumber(entry.value)}
        </span>
      ))}
    </div>
  );
}

export function CustomersChart({ data, isInitialLoading }: CustomersChartProps) {
  if (isInitialLoading) return <ChartCardSkeleton />;

  const latestNew = data[data.length - 1]?.newCustomers ?? 0;

  return (
    <GlassCard noPadding className="vania-chart-card" data-animate="fade-in">
      <div className="vania-chart-card__header">
        <div className="flex items-center gap-3 min-w-0">
          <span className="vania-icon-badge vania-icon-badge--violet" aria-hidden="true">
            <Users size={18} strokeWidth={2.5} />
          </span>
          <div className="min-w-0">
            <span className="vania-insight-eyebrow">تحلیل مشتریان</span>
            <h3 className="text-sm font-semibold text-[var(--vania-text-primary,#f5f6fa)] mt-0.5">
              مشتریان جدید هفتگی
            </h3>
            <p className="text-xs text-[var(--vania-text-tertiary,#6b7280)] mt-1">۸ هفته گذشته</p>
          </div>
        </div>
        <span className="vania-badge vania-badge--violet shrink-0">
          <Users size={12} strokeWidth={2.5} />
          {formatNumber(latestNew)} این هفته
        </span>
      </div>

      <div className="vania-chart-card__body h-64 w-full" dir="ltr">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 10, right: 8, left: 0, bottom: 0 }} barGap={4}>
            <CartesianGrid
              strokeDasharray="3 6"
              stroke="var(--vania-border-glass, rgba(255,255,255,0.08))"
              vertical={false}
            />
            <XAxis
              dataKey="week"
              tick={{ fill: 'var(--vania-text-tertiary, #6b7280)', fontSize: 11 }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              tick={{ fill: 'var(--vania-text-tertiary, #6b7280)', fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              width={32}
            />
            <Tooltip content={<CustomersTooltip />} cursor={{ fill: 'var(--vania-surface-2, rgba(255,255,255,0.04))' }} />
            <Legend
              iconType="circle"
              iconSize={8}
              wrapperStyle={{ fontSize: 12, color: 'var(--vania-text-secondary, #a3aab8)' }}
              formatter={(value) => (value === 'newCustomers' ? 'مشتری جدید' : 'مشتری بازگشتی')}
            />
            <Bar dataKey="newCustomers" fill="var(--vania-accent-teal, #2fe0c0)" radius={[6, 6, 0, 0]} maxBarSize={22} />
            <Bar dataKey="returningCustomers" fill="var(--vania-accent-violet, #7c6cff)" radius={[6, 6, 0, 0]} maxBarSize={22} opacity={0.55} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </GlassCard>
  );
}
